(function (global) {
  'use strict';
  if (global.MSPurchaseJourney) return;
  const VERSION = 'ms-purchase-journey-v1';
  const KEY = 'ms_purchase_analytics_session_v1';
  const IDLE = 1800000;
  const MAX_QUEUE = 32;
  const EVENTS = new Set(['ENTRY_SEEN','UI_OPENED','SELECTION_PRESENTED','ACTIVE_SELECTION','CALCULATION_COMPLETED','CART_ADD_ATTEMPTED','CART_ADD_CONFIRMED','CART_RETAINED','FULFILLMENT_ABANDONED','TOTAL_PRESENTED','CHECKOUT_HANDOFF','UI_ERROR']);
  const ERRORS = new Set(['CALCULATION_INVALID','DELIVERY_UNAVAILABLE','CART_ADD_FAILED','FULFILLMENT_CANCELLED','TELEMETRY_UNAVAILABLE','UNKNOWN']);
  const ROOT = '[data-ms-calculator-host], [data-calculator-type], [data-mspa-family], [data-ms-standard-purchase], [data-ms-purchase-surface]';
  let contexts = new WeakMap();
  let pendingCalculations = new WeakMap();
  const operations = new Map();
  const operationSessions = new Map();
  let gestures = new WeakMap();
  const seen = new Set();
  const queued = [];
  const observationTimers = new Map();
  const observedElements = new Map();
  let sender = null, sending = false, session = null, lastContext = null, lastContextAt = 0, lastHumanAt = 0, quickOpenIntent = null, commerceJourneyId = null, isSystemLine = null, consentGeneration = 0;
  let dropped = 0, sent = 0, sequence = 0, activeAbort = null;

  function consent() {
    try {
      const privacy = global.Shopify && global.Shopify.customerPrivacy;
      return Boolean(privacy && typeof privacy.analyticsProcessingAllowed === 'function' && privacy.analyticsProcessingAllowed());
    } catch (_) { return false; }
  }
  function purge() {
    queued.length = 0; session = null; lastContext = null; quickOpenIntent = null; contexts = new WeakMap(); pendingCalculations = new WeakMap(); operations.clear(); operationSessions.clear(); confirmedOperations.clear(); seen.clear(); gestures = new WeakMap(); consentGeneration += 1;
    if (activeAbort) activeAbort.abort();
    try { global.sessionStorage.removeItem(KEY); } catch (_) {}
    observationTimers.forEach(timer => global.clearTimeout(timer)); observationTimers.clear();
  }
  function id(prefix) {
    try {
      const bytes = new Uint8Array(16); global.crypto.getRandomValues(bytes);
      return prefix + Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
    } catch (_) { return null; }
  }
  function getSession(touch) {
    if (!consent()) { purge(); return null; }
    const now = Date.now();
    if (!session) {
      try {
        const value = JSON.parse(global.sessionStorage.getItem(KEY) || 'null');
        if (value && /^[A-Za-z0-9_-]{16,128}$/.test(value.id) && Number.isSafeInteger(value.started)
          && Number.isSafeInteger(value.last) && value.started <= now && value.last >= value.started && value.last <= now
          && now - value.last < IDLE) session = value;
      } catch (_) {}
    }
    if (!session || now - session.last >= IDLE || now < session.last) {
      const fresh = id('pj_');
      if (!fresh) return null;
      session = { id: fresh, started: now, last: now };
      contexts = new WeakMap(); pendingCalculations = new WeakMap(); lastContext = null;
    }
    if (touch) session.last = now;
    try { global.sessionStorage.setItem(KEY, JSON.stringify(session)); } catch (_) {}
    return session;
  }
  function enumValue(value, allowed, fallback) { return allowed.includes(value) ? value : fallback; }
  function safeText(value, limit) {
    return typeof value === 'string' && value.length > 0 && value.length <= limit && !/[\x00-\x1f\x7f]/.test(value) ? value : null;
  }
  function slug(value) { return String(value || '').toLowerCase().replace(/[^a-z0-9_.-]+/g, '-').replace(/^-|-$/g, '').slice(0, 40); }
  function attr(root, name) { try { const direct=root && root.getAttribute(name); if (direct) return direct; const config=root && root.querySelector && root.querySelector('[data-mspa-family-config], [data-calculator-type]'); return config && config.getAttribute(name) || ''; } catch (_) { return ''; } }
  function rootOf(value) {
    try { return value && value.closest ? value.closest('[data-ms-calculator-host]') || value.closest(ROOT) : null; } catch (_) { return null; }
  }
  function normalizeContext(value) {
    value = value && typeof value === 'object' ? value : {};
    const result = {
      surface: enumValue(value.surface, ['product_page','quick_shop','unknown'], 'unknown'),
      selection_method: enumValue(value.selection_method, ['direct_quantity','calculator','pick_mix','combined','unknown'], 'unknown'),
      cart_origin: enumValue(value.cart_origin, ['purchase_panel','stock_delivery_module','other_known','unknown'], 'unknown')
    };
    for (const [key, limit] of [['calculator_id',96],['calculator_name',120],['calculator_type',96],['calculator_version',64],['product_family',96],['product_id',40]]) {
      const clean = safeText(value[key], limit);
      if (clean && (key !== 'calculator_id' || /^[a-zA-Z0-9_.:-]{1,96}$/.test(clean))) result[key] = clean;
    }
    result.device_class = enumValue(value.device_class, ['mobile','desktop','unknown'],
      typeof global.matchMedia === 'function' ? global.matchMedia('(max-width: 740px)').matches ? 'mobile' : 'desktop' : 'unknown');
    if (Array.isArray(value.selection_methods_used)) {
      const methods = [...new Set(value.selection_methods_used.filter(item => ['direct_quantity','calculator','pick_mix'].includes(item)))];
      if (methods.length) result.selection_methods_used = Object.freeze(methods);
    }
    return Object.freeze(result);
  }
  function context(root, overrides) {
    root = rootOf(root) || root;
    const old = root && contexts.get(root) || {};
    const type = attr(root, 'data-calculator-type') || attr(root,'data-ms-calculator-type') || attr(root,'data-mspa-family');
    // Port names carry implementation versions; the calculator family/type is the stable identity.
    const family = attr(root, 'data-mspa-family') || type;
    const base = {
      surface: attr(root, 'data-calculator-context') === 'quick-shop' || attr(root,'data-mspa-context') === 'quick-shop' || attr(root,'data-ms-purchase-context') === 'quick-shop' || root && root.hasAttribute && root.hasAttribute('data-ms-quick-shop-calculator') ? 'quick_shop' : root ? 'product_page' : 'unknown',
      selection_method: 'direct_quantity', cart_origin: 'purchase_panel',
      product_id: attr(root, 'data-product-id') || attr(root,'data-mspa-product-id'), product_family: family,
      calculator_type: type, calculator_id: type ? slug(family) + ':' + slug(type) : '',
      calculator_name: attr(root, 'data-calculator-name') || family || type,
      calculator_version: attr(root, 'data-mspa-family-port-version')
    };
    return normalizeContext(Object.assign(base, old, overrides || {}));
  }
  function contextForEvent(value) {
    const ctx = normalizeContext(value);
    const calculator = ctx.selection_method === 'calculator'
      || ctx.selection_method === 'combined' && (ctx.selection_methods_used || []).includes('calculator');
    if (calculator) return ctx;
    // A calculator hosted on the page is not evidence that this action used it.
    const plain = Object.assign({}, ctx);
    ['calculator_id','calculator_name','calculator_type','calculator_version','product_family'].forEach(key => delete plain[key]);
    return Object.freeze(plain);
  }
  function snapshot(root, overrides) {
    if (root) return context(root, overrides);
    return normalizeContext(Object.assign({}, Date.now() - lastContextAt < 5000 ? lastContext : {}, overrides || {}));
  }
  function details(value) {
    const out = {};
    if (!value || typeof value !== 'object') return out;
    if (['ok','empty','not_deliverable','unavailable','cancelled','error','unknown'].includes(value.outcome)) out.outcome = value.outcome;
    for (const key of ['item_count','quantity_total','value_cents','duration_ms']) if (Number.isSafeInteger(value[key]) && value[key] >= 0) out[key] = value[key];
    if (value.error_code) out.error_code = ERRORS.has(value.error_code) ? value.error_code : 'UNKNOWN';
    if (/^[A-Za-z0-9_.:-]{8,160}$/.test(value.calculation_id || '')) out.calculation_id = value.calculation_id;
    return out;
  }
  function emit(event, ctx, values, options) {
    try {
      if (!EVENTS.has(event)) return false;
      if (!consent()) { purge(); return false; }
      const analytic = options && options.analyticSession || getSession(false);
      if (!analytic) return false;
      const operationId = options && options.operationId;
      const contextValue = contextForEvent(ctx);
      if (event === 'CALCULATION_COMPLETED' && !contextValue.calculator_id) return false;
      if (['CART_ADD_ATTEMPTED','CART_ADD_CONFIRMED','CART_RETAINED','FULFILLMENT_ABANDONED'].includes(event)
        && !/^[A-Za-z0-9_.:-]{8,160}$/.test(operationId || '')) return false;
      const clean = details(values);
      if (event === 'UI_ERROR' && !clean.error_code) return false;
      const once = options && options.once;
      const dedupe = once ? [analytic.id,event,once].join('|') : operationId ? [analytic.id,event,operationId].join('|') : null;
      if (dedupe && seen.has(dedupe)) return false;
      const eventId = id('pje_'); if (!eventId) return false;
      const payload = {schema:'aa-purchase-journey-event-v1',session_id:analytic.id,
        session_started_at:Math.floor(analytic.started / 1000),occurred_at:Math.floor(Date.now()/1000),
        event_id:eventId,event,analytics_consent:true,context:contextValue};
      if (commerceJourneyId) payload.commerce_journey_id = commerceJourneyId;
      if (operationId) payload.operation_id = operationId;
      if (Object.keys(clean).length) payload.details = clean;
      const token = options && options.cartToken;
      if (typeof token === 'string' && token.length > 0 && token.length <= 512 && !/[\x00-\x20\x7f]/.test(token)) payload.cart_token = token;
      if (queued.length >= MAX_QUEUE) { dropped += 1; return false; }
      if (dedupe) { if (seen.size >= 4096) seen.delete(seen.values().next().value); seen.add(dedupe); }
      queued.push({payload,created:Date.now(),attempts:0}); flush(); return true;
    } catch (_) { return false; }
  }
  async function flush() {
    if (sending || typeof sender !== 'function') return;
    sending = true;
    try {
      while (queued.length) {
        if (!consent()) { purge(); break; }
        const entry = queued[0];
        const entryGeneration = consentGeneration;
        if (Date.now()-entry.created > 15000) { queued.shift(); dropped += 1; continue; }
        activeAbort = typeof global.AbortController === 'function' ? new global.AbortController() : null;
        let timer;
        const timeout = new Promise(resolve => { timer = global.setTimeout(() => { if (activeAbort) activeAbort.abort(); resolve(false); }, 4000); });
        let ok = false;
        try { ok = await Promise.race([Promise.resolve(sender(entry.payload,{signal:activeAbort && activeAbort.signal})),timeout]); } catch (_) {}
        global.clearTimeout(timer); activeAbort = null;
        if (!consent()) { purge(); break; }
        if (entryGeneration !== consentGeneration || queued[0] !== entry) continue;
        // Retry once with identical event ID. Never persist raw payload/token.
        entry.attempts += 1;
        if (ok === true) { queued.shift(); sent += 1; }
        else if (entry.attempts >= 2) { queued.shift(); dropped += 1; }
      }
    } finally { sending = false; }
  }
  function configure(options) { if (options && typeof options.send === 'function') { sender = options.send; isSystemLine = typeof options.isSystemLine === 'function' ? options.isSystemLine : null; commerceJourneyId = /^[A-Za-z0-9_-]{16,128}$/.test(options.commerceJourneyId || '') ? options.commerceJourneyId : null; flush(); } }
  function markSelection(root, method, extra) {
    root = rootOf(root) || root;
    const analytic = getSession(true); if (!analytic || !root) return null;
    const before = context(root);
    const used = new Set(before.selection_methods_used || []);
    if (['calculator','pick_mix','direct_quantity'].includes(method)) used.add(method);
    const next = context(root, Object.assign({}, extra || {}, {selection_method:used.size > 1 ? 'combined' : method,selection_methods_used:[...used]}));
    contexts.set(root,next); lastContext = next; lastContextAt = Date.now(); return next;
  }
  function beginOperation(operationId, ctx) {
    if (!/^[A-Za-z0-9_.:-]{8,160}$/.test(operationId || '')) return null;
    const analytic = getSession(true); if (!analytic) return null;
    if (operations.has(operationId)) return operations.get(operationId);
    const frozen = normalizeContext(ctx || snapshot());
    if (operations.size >= 128) { const oldest=operations.keys().next().value; operations.delete(oldest); operationSessions.delete(oldest); confirmedOperations.delete(oldest); }
    operations.set(operationId,frozen); operationSessions.set(operationId,Object.freeze({id:analytic.id,started:analytic.started}));
    emit('CART_ADD_ATTEMPTED',frozen,{outcome:'ok'},{operationId}); return frozen;
  }
  function operation(event, operationId, options) {
    const ctx = operations.get(operationId); if (!ctx) return false;
    const cart = options && options.cart;
    const values = Object.assign({}, options && options.details || {});
    if (cart && Number.isSafeInteger(cart.item_count)) values.item_count = cart.item_count;
    return emit(event,ctx,values,{operationId,cartToken:cart && cart.token,analyticSession:operationSessions.get(operationId)});
  }
  function cartBasis(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    try {
      const totals=new Map();
      cart.items.filter(item => !isSystemLine || !isSystemLine(item)).forEach(item => { const key=String(item.variant_id || item.id); totals.set(key,(totals.get(key)||0)+item.quantity); });
      return JSON.stringify([...totals].sort((a,b) => a[0].localeCompare(b[0])));
    } catch (_) { return null; }
  }
  const confirmedOperations = new Map();
  function confirmed(operationId,before,after,items) {
    if (!operations.has(operationId) || !before || !after || !Array.isArray(before.items) || !Array.isArray(after.items) || !Array.isArray(items) || !items.length) return false;
    const totals = rows => {
      const value = new Map();
      for (const item of rows) {
        const key = String(item.variant_id || item.id || item.variantId || item.merchandiseId || '').replace(/^gid:\/\/shopify\/ProductVariant\//,'');
        if (!/^[1-9][0-9]*$/.test(key) || !Number.isSafeInteger(item.quantity) || item.quantity < 0) return null;
        value.set(key,(value.get(key)||0)+item.quantity);
      }
      return value;
    };
    const a=totals(before.items),z=totals(after.items),expected=totals(items);
    if (!a || !z || !expected || ![...expected].every(([key,qty]) => qty > 0 && (z.get(key)||0)-(a.get(key)||0)>=qty)) return false;
    confirmedOperations.set(operationId,cartBasis(after));
    return operation('CART_ADD_CONFIRMED',operationId,{cart:after,details:{outcome:'ok'}});
  }
  function settled(cart,values) {
    const basis=cartBasis(cart); if (!basis) return false;
    let counted=false;
    confirmedOperations.forEach((expected,operationId) => {
      if (basis !== expected) return;
      counted=operation('CART_RETAINED',operationId,{cart,details:{outcome:'ok'}})||counted;
      counted=operation('TOTAL_PRESENTED',operationId,{cart,details:Object.assign({outcome:'ok'},values||{})})||counted;
    });
    return counted;
  }
  function handoff(cart) {
    const basis=cartBasis(cart); if (!basis) return false;
    let counted=false;
    confirmedOperations.forEach((expected,operationId) => {
      if (basis===expected) counted=operation('CHECKOUT_HANDOFF',operationId,{cart,details:{outcome:'ok'}})||counted;
    });
    return counted;
  }
  function quickShopHandle(value) {
    try {
      const url = new global.URL(String(value || ''), global.location.href);
      if (url.origin !== global.location.origin || url.searchParams.get('view') !== 'quick-shop') return '';
      const match = url.pathname.match(/\/products\/([^/]+)\/?$/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_) { return ''; }
  }
  function quickShopNodes(scope) {
    try {
      const product = scope && (scope.closest('[data-quick-shop-product]') || scope.querySelector('[data-quick-shop-product]'));
      const drawer = product && product.closest('[data-quick-shop-root]');
      const surface = product && (product.querySelector('[data-ms-purchase-surface][data-ms-purchase-context="quick-shop"]')
        || product.querySelector('[data-ms-calculator-host][data-calculator-context="quick-shop"]'));
      const handle = attr(product,'data-product-handle');
      if (!product || !drawer || !surface || !handle || attr(surface,'data-product-handle') !== handle) return null;
      return {product,drawer,surface,handle};
    } catch (_) { return null; }
  }
  function liveQuickShop(nodes) {
    return Boolean(nodes && nodes.product.isConnected && nodes.surface.isConnected && nodes.drawer.isConnected
      && nodes.drawer.classList.contains('is-open') && !nodes.product.closest('[hidden], [aria-hidden="true"]')
      && global.document.visibilityState !== 'hidden');
  }
  function quickShopOpened(scope) {
    const intent = quickOpenIntent, nodes = quickShopNodes(scope), generation = consentGeneration;
    if (!intent || !nodes || nodes.handle !== intent.handle) return;
    const observe = () => {
      if (quickOpenIntent !== intent || consentGeneration !== generation || !consent()
        || Date.now()-intent.at >= 10000 || !liveQuickShop(nodes)
        || quickShopNodes(scope)?.product !== nodes.product) return;
      emit('UI_OPENED',context(nodes.surface,{surface:'quick_shop',selection_method:'direct_quantity',selection_methods_used:[]}),
        {outcome:'ok'},{once:'qs:'+nodes.handle});
      quickOpenIntent=null;
    };
    if (liveQuickShop(nodes)) observe();
    // A cached document may finish before the existing 10ms drawer-open timer.
    // This bounded passive recheck remains tied to the same intent and nodes.
    else global.setTimeout(observe,32);
  }
  function quickShopQuantityStep(event, button) {
    if (event.type !== 'click' || !button || button.disabled || attr(button,'aria-disabled') === 'true') return;
    const surface = button.closest('[data-ms-purchase-surface][data-ms-purchase-kind="standard"][data-ms-purchase-context="quick-shop"]');
    const nodes = quickShopNodes(surface), input = surface && surface.querySelector('[data-ms-purchase-qty-input]');
    if (!nodes || nodes.surface !== surface || !liveQuickShop(nodes) || !input || input.disabled || input.readOnly) return;
    const before = Number(input.value), generation = consentGeneration;
    if (!/^\d+$/.test(input.value) || !Number.isSafeInteger(before) || before < 1) return;
    const expected = button.hasAttribute('data-ms-purchase-qty-plus') ? before+1 : Math.max(1,before-1);
    if (expected === before || !Number.isSafeInteger(expected)) return;
    // Read after the native click handler; never change quantity or synthesize events.
    global.setTimeout(() => {
      if (consentGeneration !== generation || !consent() || !liveQuickShop(nodes) || !button.isConnected
        || button.disabled || attr(button,'aria-disabled') === 'true' || input.disabled || input.readOnly
        || quickShopNodes(surface)?.product !== nodes.product || surface.querySelector('[data-ms-purchase-qty-input]') !== input
        || !/^\d+$/.test(input.value) || Number(input.value) !== expected) return;
      const ctx = markSelection(surface,'direct_quantity');
      if (ctx) emit('ACTIVE_SELECTION',ctx,{outcome:'ok'},{once:ctx.selection_method+':'+(ctx.product_id || 'surface')});
    },0);
  }
  function human(event) {
    if (!event || event.isTrusted !== true || !consent()) return;
    const target = event.target;
    lastHumanAt=Date.now(); getSession(true);
    const quick=target && target.closest && target.closest('[data-quick-shop-url]');
    if (quick && event.type === 'click') {
      const handle = quickShopHandle(quick.getAttribute('data-quick-shop-url'));
      quickOpenIntent = handle ? {at:Date.now(),handle} : null;
    }
    if (event.type === 'keydown' && event.key === 'Escape'
      || event.type === 'click' && target && target.closest && target.closest('[data-quick-shop-close]')) quickOpenIntent=null;
    const step = target && target.closest && target.closest('[data-ms-purchase-qty-plus], [data-ms-purchase-qty-minus]');
    if (step) quickShopQuantityStep(event,step);
    const root = rootOf(target); if (!root || !target || !target.closest) return;
    lastContext = context(root); lastContextAt = Date.now();
    const inputEvent=event.type === 'input' || event.type === 'change';
    const quantity=target.closest('[data-mspa-quantity]');
    const pick=target.closest('[data-mspa-pick-line], [data-mspa-pick-mix]')
      || /^pick_/.test(quantity && quantity.getAttribute('data-mspa-quantity') || '');
    const button=target.closest('[data-mspa-choice], [data-mspa-toggle], [data-mspa-quantity-value], [data-mspa-stepper-action]');
    const buttonEvent=button && (event.type === 'pointerdown' || event.type === 'click'
      || event.type === 'keydown' && ['Enter',' '].includes(event.key));
    if (!inputEvent && !buttonEvent) return;
    if (inputEvent && (target.validity && target.validity.valid === false || typeof target.value === 'string' && target.value.trim() === '')) return;
    if (buttonEvent) {
      const previous=gestures.get(root);
      if (event.type === 'click' && previous && Date.now()-previous.at < 1000 && previous.button === button) return;
      gestures.set(root,{at:Date.now(),button});
      const group=button.closest('[data-mspa-choice-group]');
      if (group && group.getAttribute('data-mspa-choice-group') === 'mode') {
        // Opening a mode does not prove that any value from that mode was used.
        const choice=button.getAttribute('data-mspa-choice');
        if (choice === 'pick' || choice === 'calculate') emit('UI_OPENED',context(root,{selection_method:choice === 'pick' ? 'pick_mix' : 'calculator'}),{outcome:'ok'},{once:'mode:'+choice+':'+(context(root).calculator_id || '')});
        return;
      }
    }
    const preset=button && button.closest('[data-mspa-quantity-preset], [data-mspa-stepper]');
    const direct=Boolean(quantity || preset);
    const calculator=Boolean(attr(root,'data-calculator-type') || attr(root,'data-ms-calculator-type') || attr(root,'data-mspa-family'));
    const ctx=pick ? markSelection(root,'pick_mix') : direct || !calculator ? markSelection(root,'direct_quantity') : context(root,{selection_method:'calculator'});
    if (!ctx) return;
    emit('ACTIVE_SELECTION',ctx,{outcome:'ok'},{once:ctx.selection_method+':'+(ctx.calculator_id || ctx.product_id || 'surface')});
    if (calculator && !pick && !direct) {
      emit('UI_OPENED',ctx,{outcome:'ok'},{once:'calculator:'+ctx.calculator_id});
      pendingCalculations.set(root,{at:Date.now(),id:id('pjc_')});
    }
  }
  function calculated(root, state, definition) {
    root=rootOf(root) || root;
    const pending = root && pendingCalculations.get(root);
    if (!pending || Date.now()-pending.at > 2000 || !state || !consent()) return false;
    if (state.error === true) {
      pendingCalculations.delete(root);
      return emit('UI_ERROR',context(root,{selection_method:'calculator'}),{outcome:'error',error_code:'CALCULATION_INVALID'},{once:'calc-error:'+pending.id});
    }
    if (state.ready !== true) return false;
    pendingCalculations.delete(root);
    // A pick-and-mix result is manual selection, never a calculation.
    try { if (definition && typeof definition.stockContext === 'function' && definition.stockContext().mode === 'pick') return false; } catch (_) { return false; }
    const extra = {};
    const family = safeText(state.family,96);
    if (family) { extra.product_family = family; extra.calculator_id = slug(family)+':'+slug(attr(root,'data-calculator-type') || family); }
    const label = definition && safeText(definition.name || definition.title || definition.label,120);
    const version = definition && safeText(String(definition.version || ''),64);
    if (label) extra.calculator_name = label;
    if (version) extra.calculator_version = version;
    const ctx = markSelection(root,'calculator',extra);
    return ctx ? emit('CALCULATION_COMPLETED',ctx,{outcome:'ok',calculation_id:pending.id}) : false;
  }
  function observeElement(element, ctx) {
    if (!element || typeof global.IntersectionObserver !== 'function') return;
    const observer = new global.IntersectionObserver(entries => entries.forEach(entry => {
      const old = observationTimers.get(entry.target);
      if (old) { global.clearTimeout(old); observationTimers.delete(entry.target); }
      if (entry.isIntersecting && entry.intersectionRatio >= 0.5 && global.document.visibilityState !== 'hidden') {
        const timer = global.setTimeout(() => {
          observationTimers.delete(entry.target);
          if (!consent() || global.document.visibilityState === 'hidden' || entry.target.isConnected === false) return;
          const contextValue = typeof ctx === 'function' ? ctx() : ctx;
          emit('ENTRY_SEEN',contextValue,{outcome:'ok'},{once:JSON.stringify(contextValue)});
        },1000);
        observationTimers.set(entry.target,timer);
      }
    }),{threshold:[0,0.5,1]});
    observedElements.set(element,observer); observer.observe(element); return observer;
  }
  function scan(scope) {
    if (!scope || !scope.querySelectorAll) return;
    scope.querySelectorAll('[data-quick-shop-url]').forEach(button => {
      if (button.getAttribute('data-ms-journey-qs-observed') === 'true') return;
      button.setAttribute('data-ms-journey-qs-observed','true');
      observeElement(button,() => normalizeContext({surface:'quick_shop',selection_method:'direct_quantity',cart_origin:'purchase_panel'}));
    });
    scope.querySelectorAll(ROOT).forEach(root => {
      const rootObserved=root.getAttribute('data-ms-journey-observed') === 'true';
      root.setAttribute('data-ms-journey-observed','true');
      const entry = root.querySelector && root.querySelector('[data-mspa-cta], [data-ms-purchase-submit]') || root;
      if (!rootObserved) observeElement(entry,() => context(root));
      if (root.querySelectorAll) root.querySelectorAll('[data-mspa-control], [data-mspa-select], [data-mspa-quantity]').forEach(control => {
        if (control.getAttribute('data-ms-journey-field-observed') === 'true') return;
        control.setAttribute('data-ms-journey-field-observed','true');
        const pick=control.closest('[data-mspa-pick-line]') || /^pick_/.test(control.getAttribute('data-mspa-quantity') || control.getAttribute('data-mspa-control') || '');
        const method=pick ? 'pick_mix' : control.hasAttribute('data-mspa-quantity') ? 'direct_quantity' : 'calculator';
        observeElement(control,() => context(root,{selection_method:method}));
      });
    });
  }
  function bind() {
    const document = global.document; if (!document || !document.addEventListener) return;
    ['input','change','click','pointerdown','keydown'].forEach(name => document.addEventListener(name,human,true));
    document.addEventListener('visitorConsentCollected',() => { if (!consent()) purge(); else { scan(document); observedElements.forEach((observer,element) => { observer.unobserve(element); observer.observe(element); }); } });
    document.addEventListener('visibilitychange',() => { if (document.visibilityState === 'hidden') { observationTimers.forEach(timer => global.clearTimeout(timer)); observationTimers.clear(); } else observedElements.forEach((observer,element) => { observer.unobserve(element); observer.observe(element); }); });
    document.addEventListener('ms:quick-shop:loaded',event => quickShopOpened(event.detail && (event.detail.content || event.detail.container)));
    document.addEventListener('ms:calculator:quick-open',event => quickShopOpened(event.detail && event.detail.root));
    document.addEventListener('ms:mspa-motor:mount',() => scan(document));
    scan(document);
    if (typeof global.MutationObserver === 'function') new global.MutationObserver(() => scan(document)).observe(document.documentElement,{childList:true,subtree:true});
  }
  global.MSPurchaseJourney = Object.freeze({version:VERSION,configure,emit,context,snapshot,markSelection,beginOperation,operation,confirmed,settled,handoff,calculated,observeElement,
    operationId:() => id('pjo_'),diagnostics:() => Object.freeze({sent,dropped,queued:queued.length}),consentChanged:() => { if (!consent()) purge(); },
    // No raw event/session/cart payload is exposed by diagnostics.
    normalizeContext,activity:() => Boolean(getSession(true))});
  if (global.document && global.document.readyState === 'loading') global.document.addEventListener('DOMContentLoaded',bind,{once:true}); else bind();
})(typeof window !== 'undefined' ? window : globalThis);
